/**
 * Detalle de un curso del catálogo. Muestra el temario y permite INSCRIBIRSE
 * (progreso local-first).
 *
 * Fuente de datos en DOS niveles (LF-B):
 *  1. REST guest-first (`/api/catalog/course/:id`) — cursos PÚBLICOS, también
 *     para invitados sin sync.
 *  2. Fallback a los datos SINCRONIZADOS: un curso `visibility='org'` NO está
 *     en el catálogo público (REST 404), pero sus filas (course/unit/lesson)
 *     SÍ bajan por sync a los miembros de la org — sin este fallback un
 *     miembro veía "Course not found" sobre un curso al que sí tiene acceso.
 *     El mismo fallback cubre el detalle OFFLINE de cursos ya sincronizados.
 *
 * Si ninguna fuente resuelve, `createEntityResolution` distingue loading /
 * not-found / offline (nada de spinners eternos ni 404 mentirosos).
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { msg } from "@lingui/core/macro";
import { BookOpen, CheckCircle2, PlayCircle, Star, Users } from "lucide-solid";
import {
  courseQueryKeys,
  type CourseTreeDTO,
  enrollMutation,
  fetchCatalogCourse,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link, useNavigate } from "@/lib/u-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useCourseTree, useCourseWrite, useEnrollment } from "../../hooks/use-course-data";
import { CourseCohortEnroll } from "@/features/academy/components/course-cohort-enroll";

/** Modelo de vista normalizado (REST público o filas sincronizadas). */
interface CourseView {
  title: string;
  description: string | null;
  lessonCount: number;
  /** Solo lo conoce el REST público; en fallback sync se oculta. */
  enrolledCount: number | null;
  units: {
    id: string;
    title: string;
    lessons: { id: string; title: string; blockCount: number | null; xp: number }[];
  }[];
}

export const CourseDetailView: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db: syncDb } = usePowerSyncContext();
  const { drizzle, userId } = useCourseWrite();
  const enrollment = useEnrollment(() => props.courseId);
  const isEnrolled = () => Boolean(enrollment.data && !enrollment.data.deleted_at);

  const tree = createQuery<CourseTreeDTO>(() => ({
    queryKey: courseQueryKeys.catalogCourse(props.courseId),
    queryFn: ({ signal }) => fetchCatalogCourse(SERVER_URL, props.courseId, { signal }),
    staleTime: 30_000,
    // Un 404 de curso org-visible no se arregla reintentando; el fallback
    // sincronizado responde al instante.
    retry: 1,
  }));

  // Fallback local-first: el curso puede estar sincronizado (miembro de la org
  // del curso, o curso ya descargado) aunque el catálogo público lo niegue.
  const local = useCourseTree(() => props.courseId);

  // El REST "decidió": tiene dato, falló, o quedó PAUSADO offline (networkMode
  // offlineFirst deja la query pending+paused sin red — sin este caso el
  // detalle offline se quedaba en skeleton eterno en vez de caer al fallback).
  const restSettled = () => !tree.isPending || tree.fetchStatus === "paused";

  const view = createMemo<CourseView | null>(() => {
    const dto = tree.data;
    if (dto) {
      return {
        title: dto.course.title,
        description: dto.course.description ?? null,
        lessonCount: dto.units.reduce((n, u) => n + u.lessons.length, 0),
        enrolledCount: dto.enrolledCount,
        units: dto.units.map((u) => ({
          id: u.unit.id,
          title: u.unit.title,
          lessons: u.lessons.map((l) => ({
            id: l.lesson.id,
            title: l.lesson.title,
            blockCount: l.blocks.length,
            xp: l.lesson.xpReward,
          })),
        })),
      };
    }
    // El REST sigue en vuelo → aún no decidimos (evita el flash del fallback).
    if (!restSettled()) {
      return null;
    }
    const course = local.course.data;
    if (!course) {
      return null;
    }
    const lessons = local.lessons.data ?? [];
    return {
      title: course.title ?? "",
      description: course.description ?? null,
      lessonCount: lessons.length,
      enrolledCount: null,
      units: (local.units.data ?? []).map((u) => ({
        id: u.id,
        title: u.title ?? "",
        lessons: lessons
          .filter((l) => l.unit_id === u.id)
          .map((l) => ({
            id: l.id,
            title: l.title ?? "",
            blockCount: null,
            xp: l.xp_reward ?? 0,
          })),
      })),
    };
  });

  /** El detalle viene del fallback sincronizado (no del catálogo público). */
  const isSyncFallback = () => !tree.data && Boolean(local.course.data);

  // Sin REST y sin fila local: ¿cargando, no existe, o simplemente offline y
  // sin sincronizar? (resolución canónica sobre el estado de sync).
  const resolution = createEntityResolution({
    db: syncDb,
    hasEntity: () => Boolean(view()),
    isPending: () => !restSettled() || local.course.isLoading,
  });

  const enroll = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    await enrollMutation(db, uid, props.courseId);
    showToast(t(msg`Enrolled — your progress is saved on this device`), "success");
    void navigate({ to: "/learn/$courseId", params: { courseId: props.courseId } });
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <Link
        to="/catalog"
        class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent"
      >
        ← {t(msg`Catalog`)}
      </Link>
      <Show
        when={view()}
        fallback={
          <Show
            when={resolution() === "loading"}
            fallback={
              <div data-testid="course-not-found">
                <NotFoundState
                  variant={resolution() === "offline" ? "offline" : "not-found"}
                  title={t(msg`Course not available`)}
                  description={
                    resolution() === "offline"
                      ? t(
                          msg`You're offline and this course hasn't synced to this device yet. Reconnect and try again.`,
                        )
                      : t(msg`This course doesn't exist or is not available to you.`)
                  }
                  primaryAction={{
                    label: t(msg`Back to catalog`),
                    onClick: () => void navigate({ to: "/catalog" }),
                  }}
                />
              </div>
            }
          >
            <Skeleton class="h-44 w-full rounded-3xl" />
          </Show>
        }
      >
        {(data) => (
          <>
            <div class="space-mesh rounded-3xl border border-border p-6">
              <div class="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md shadow-accent/25">
                <BookOpen class="h-7 w-7" />
              </div>
              <h1
                class="font-display text-2xl font-extrabold tracking-tight md:text-3xl"
                data-testid="course-title"
              >
                {data().title}
              </h1>
              <Show when={data().description}>
                <p class="mt-2 text-muted-foreground">{data().description}</p>
              </Show>
              <div class="mt-4 flex flex-wrap items-center gap-2">
                <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/12 px-3 py-1 text-xs font-semibold text-accent">
                  <BookOpen class="h-3.5 w-3.5" />{" "}
                  {t(msg`{count} lessons`, { count: data().lessonCount })}
                </span>
                <Show when={data().enrolledCount != null}>
                  <span class="inline-flex items-center gap-1.5 rounded-full bg-joy-sky/15 px-3 py-1 text-xs font-semibold text-joy-sky">
                    <Users class="h-3.5 w-3.5" />{" "}
                    {t(msg`{count} enrolled`, { count: data().enrolledCount })}
                  </span>
                </Show>
                {/* Curso privado de una academia, servido desde sync. */}
                <Show when={isSyncFallback()}>
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground"
                    data-testid="course-org-fallback"
                  >
                    {t(msg`Academy course`)}
                  </span>
                </Show>
              </div>
              <div class="mt-5">
                <Show
                  when={!isEnrolled()}
                  fallback={
                    <Button
                      data-testid="course-continue"
                      onClick={() =>
                        navigate({ to: "/learn/$courseId", params: { courseId: props.courseId } })
                      }
                      class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                    >
                      <PlayCircle class="h-4 w-4" /> {t(msg`Continue`)}
                    </Button>
                  }
                >
                  <Button
                    data-testid="course-enroll"
                    onClick={enroll}
                    // LF-B: sin la BD local lista, el clic era un no-op SILENCIOSO
                    // (guard `if (!db) return`) — deshabilitado hasta que el
                    // write-path local esté disponible.
                    disabled={!drizzle() || !userId()}
                    class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                  >
                    <CheckCircle2 class="h-4 w-4" /> {t(msg`Enroll`)}
                  </Button>
                </Show>
              </div>
            </div>

            {/* F2-A: cohortes abiertas de mis academias para este curso —
                selección + solicitud de matrícula (server-authoritative). */}
            <CourseCohortEnroll courseId={props.courseId} />

            {/* Temario */}
            <div class="space-y-4" data-testid="course-syllabus">
              <For each={data().units}>
                {(u) => (
                  <div class="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                    <h3 class="font-display font-bold">{u.title}</h3>
                    <ul class="mt-2 space-y-1">
                      <For each={u.lessons}>
                        {(l) => (
                          <li class="flex items-center justify-between rounded-xl px-2 py-2 text-sm transition-colors hover:bg-accent/5">
                            <span class="font-medium">{l.title}</span>
                            <span class="flex items-center gap-2 text-xs text-muted-foreground">
                              <Show when={l.blockCount != null}>
                                <span>{t(msg`{count} blocks`, { count: l.blockCount })}</span>
                              </Show>
                              <span class="inline-flex items-center gap-1 font-bold text-foreground">
                                <Star class="h-3 w-3 fill-current text-joy-sun" />
                                {l.xp}
                              </span>
                            </span>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                )}
              </For>
            </div>
          </>
        )}
      </Show>
    </div>
  );
};
